<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';

const { content, createdAt } = useMessageContext();
const { locale } = useI18n();

const activityDate = computed(() => {
  const rawTimestamp = Number(createdAt.value);
  if (!Number.isFinite(rawTimestamp)) return null;

  const timestamp =
    rawTimestamp > 1_000_000_000_000 ? rawTimestamp : rawTimestamp * 1000;
  return new Date(timestamp);
});

const readableTime = computed(() => {
  if (!activityDate.value) return '';

  return new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(activityDate.value);
});

const machineTime = computed(() => activityDate.value?.toISOString() || '');

const eventAppearance = computed(() => {
  const normalizedContent = String(content.value || '').toLocaleLowerCase();

  if (
    normalizedContent.includes('reabert') ||
    normalizedContent.includes('reopen')
  ) {
    return {
      icon: 'i-lucide-rotate-ccw',
      tone: 'bg-n-teal-3 text-n-teal-11',
    };
  }

  if (
    normalizedContent.includes('prioridade') ||
    normalizedContent.includes('priority')
  ) {
    return {
      icon: 'i-lucide-flag',
      tone: 'bg-n-amber-3 text-n-amber-11',
    };
  }

  if (
    normalizedContent.includes('adicionou') ||
    normalizedContent.includes('removeu') ||
    normalizedContent.includes('label') ||
    normalizedContent.includes('etiqueta')
  ) {
    return {
      icon: 'i-lucide-tag',
      tone: 'bg-n-brand/10 text-n-brand',
    };
  }

  if (
    normalizedContent.includes('atribuiu') ||
    normalizedContent.includes('assigned')
  ) {
    return {
      icon: 'i-lucide-user-round-check',
      tone: 'bg-n-brand/10 text-n-brand',
    };
  }

  if (
    normalizedContent.includes('resolvid') ||
    normalizedContent.includes('resolved')
  ) {
    return {
      icon: 'i-lucide-circle-check',
      tone: 'bg-n-teal-3 text-n-teal-11',
    };
  }

  return {
    icon: 'i-lucide-history',
    tone: 'bg-n-alpha-2 text-n-slate-11',
  };
});
</script>

<template>
  <div
    class="my-1.5 flex w-full items-center justify-center px-4"
    data-bubble-name="activity"
  >
    <div
      class="flex max-w-[min(92%,48rem)] items-center gap-2.5 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 shadow-sm"
    >
      <span
        class="grid size-7 shrink-0 place-content-center rounded-lg"
        :class="eventAppearance.tone"
        aria-hidden="true"
      >
        <span :class="eventAppearance.icon" class="size-3.5" />
      </span>
      <span
        class="min-w-0 flex flex-wrap items-center justify-center gap-x-1.5 text-center text-xs leading-5"
      >
        <span class="font-medium text-n-slate-11" :title="content">
          {{ content }}
        </span>
        <span
          v-if="readableTime"
          class="size-0.5 rounded-full bg-n-slate-9"
          aria-hidden="true"
        />
        <time
          v-if="readableTime"
          :datetime="machineTime"
          class="whitespace-nowrap font-medium tabular-nums text-n-slate-10"
        >
          {{ readableTime }}
        </time>
      </span>
    </div>
  </div>
</template>
